/* AgentWeave — the Agent IO story as a picture: specialist agents from the
   org's library get WOVEN together by an nQube coordinator block, and the
   whole swarm rides the nQube backbone — where a model can propose but only
   a gate (human review / keyword filter) reaches a side effect, and every
   side effect names a declared capability (shared/blocks/agentosGraph.js).

   Read-only React Flow (fitView camera, no pan/zoom), same pattern as
   KitIpDiagram. Pass the org's real agents when available; the catalog
   families stand in otherwise. `compact` renders the marketing variant. */
import React, { useEffect, useMemo } from 'react';
import ReactFlow, {
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FAMILIES } from '../data/agentCatalog';
import './AgentWeave.css';

const SURF = '#14e0c8';
const GATE = '#f87171';
const CAP = '#4ade80';
const DIM = '#9aa0a6';

function WeaveNode({ data }) {
  return (
    <div className={`aw-card aw-card-${data.tone}`}>
      <Handle type="target" position={Position.Top} id="t" />
      <Handle type="target" position={Position.Left} id="l" />
      <Handle type="source" position={Position.Bottom} id="b" />
      <Handle type="source" position={Position.Right} id="r" />
      {data.kicker && <span className="aw-kicker">{data.kicker}</span>}
      <strong>{data.title}</strong>
      {data.body && <p>{data.body}</p>}
    </div>
  );
}

function LaneNode({ data }) {
  return (
    <div className="aw-lane">
      <span className="aw-lane-label">{data.label}</span>
    </div>
  );
}

const nodeTypes = { weave: WeaveNode, lane: LaneNode };

const still = { draggable: false, selectable: false, connectable: false };

function edge(id, source, target, opts = {}) {
  const color = opts.color || DIM;
  return {
    id,
    source,
    target,
    sourceHandle: opts.sourceHandle || 'b',
    targetHandle: opts.targetHandle || 't',
    type: 'default',
    animated: !!opts.animated,
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
    style: { stroke: color, strokeWidth: opts.animated ? 2 : 1.5, ...(opts.dashed ? { strokeDasharray: '6 4' } : {}) },
  };
}

function FitFrame({ paneClass }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    const pane = document.querySelector(`.${paneClass}`);
    if (!pane) return undefined;
    const fit = () => fitView({ padding: 0.06, duration: 0 });
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(pane);
    return () => ro.disconnect();
  }, [fitView, paneClass]);
  return null;
}

/* Layout: specialists across the top, the coordinator weaving them in the
   middle, and the nQube backbone lane (compute → gate → capability) below. */
function buildGraph(agents, compact) {
  const max = compact ? 4 : 6;
  const roster = (agents && agents.length
    ? agents.map((a) => ({ id: a.id, name: a.name || a.role || 'agent', line: a.role || '' }))
    : FAMILIES.map((f) => ({ id: f.id, name: f.name, line: f.line }))
  ).slice(0, max);

  const SW = compact ? 128 : 150; // specialist card width
  const GAP = compact ? 16 : 22;
  const rowW = roster.length * SW + (roster.length - 1) * GAP;

  const BW = compact ? 176 : 210; // backbone card width
  const laneW = Math.max(rowW, 3 * BW + 2 * 34 + 48);
  const cx = laneW / 2;

  const specY = 0;
  const coordY = compact ? 130 : 170;
  const laneY = compact ? 250 : 320;
  const backY = laneY + (compact ? 34 : 40);

  const nodes = [];
  const edges = [];

  // The backbone lane background sits behind its three stations.
  nodes.push({
    id: 'lane',
    type: 'lane',
    position: { x: 0, y: laneY },
    style: { width: laneW, height: compact ? 116 : 138, pointerEvents: 'none' },
    data: { label: 'nQube backbone' },
    ...still,
    focusable: false,
  });

  const specX0 = cx - rowW / 2;
  roster.forEach((r, i) => {
    const id = `spec-${r.id}`;
    nodes.push({
      id,
      type: 'weave',
      position: { x: specX0 + i * (SW + GAP), y: specY },
      style: { width: SW },
      data: { tone: 'spec', kicker: compact ? null : 'specialist', title: r.name, body: compact ? null : r.line },
      ...still,
    });
    edges.push(edge(`e-${id}`, id, 'coord', { color: SURF, animated: true }));
  });

  nodes.push({
    id: 'coord',
    type: 'weave',
    position: { x: cx - (compact ? 96 : 116), y: coordY },
    style: { width: compact ? 192 : 232 },
    data: {
      tone: 'coord',
      kicker: 'control.coordinate',
      title: 'Coordinator',
      body: compact ? 'Weaves roles on one objective' : 'Names roles from the library, runs them in parallel then in order, loops until the objective is met. It cannot commit.',
    },
    ...still,
  });

  const backX0 = cx - (3 * BW + 2 * 34) / 2;
  nodes.push({
    id: 'compute',
    type: 'weave',
    position: { x: backX0, y: backY },
    style: { width: BW },
    data: { tone: 'nqube', kicker: 'blocks', title: 'nQube compute', body: compact ? 'Shared, dedicated, GPU' : 'Every role runs as a block on shared, dedicated, or GPU tiers.' },
    ...still,
  });
  nodes.push({
    id: 'gate',
    type: 'weave',
    position: { x: backX0 + BW + 34, y: backY },
    style: { width: BW },
    data: { tone: 'gate', kicker: 'control.review / filter', title: 'The gate', body: compact ? 'No model reaches a side effect' : 'A non-model gate sits on every path to a side effect — human review or a keyword filter.' },
    ...still,
  });
  nodes.push({
    id: 'cap',
    type: 'weave',
    position: { x: backX0 + 2 * (BW + 34), y: backY },
    style: { width: BW },
    data: { tone: 'cap', kicker: 'declared capability', title: 'Side effect', body: compact ? 'Webhook, dataset, S3 — named' : 'Webhook, dataset, S3, knowledge — each names a capability declared on the pipeline.' },
    ...still,
  });

  edges.push(edge('e-coord-compute', 'coord', 'compute', { color: SURF, animated: true, sourceHandle: 'b', targetHandle: 't' }));
  edges.push(edge('e-compute-gate', 'compute', 'gate', { color: GATE, sourceHandle: 'r', targetHandle: 'l' }));
  edges.push(edge('e-gate-cap', 'gate', 'cap', { color: CAP, sourceHandle: 'r', targetHandle: 'l' }));

  return { nodes, edges };
}

export default function AgentWeave({ agents = null, compact = false }) {
  const { nodes, edges } = useMemo(() => buildGraph(agents, compact), [agents, compact]);
  const paneClass = compact ? 'aw-canvas-compact' : 'aw-canvas-full';
  return (
    <div className={`aw ${compact ? 'aw-compact' : ''}`}>
      <div className={`aw-canvas ${paneClass}`}>
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.06 }}
            panOnDrag={false}
            panOnScroll={false}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            preventScrolling={false}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            nodesFocusable={false}
            edgesFocusable={false}
            proOptions={{ hideAttribution: true }}
            style={{ background: 'transparent' }}
          >
            <FitFrame paneClass={paneClass} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
      {!compact && (
        <p className="aw-caption">
          Specialists from your library, woven by a coordinator block, riding the nQube
          backbone — where no model reaches a side effect without a gate, and every side
          effect names a declared capability.
        </p>
      )}
    </div>
  );
}
